import type { Error as ErrorEnvelope } from '@/lib/api/generated/model';

/**
 * The refusals the connections endpoints can answer with, one class each —
 * same reasoning `features/secrets/errors.ts` and `features/triggers/errors.ts`
 * apply to their own error sets.
 *
 * Backend codes, per `product-x-next-service`'s
 * `app/features/connections/errors.py`: `CONNECTION_NOT_FOUND` (404), `CONNECTION_ALREADY_EXISTS` (409),
 * `CONNECTION_CONFIG_INVALID` (422), `CONNECTION_SECRET_REFERENCE_NOT_FOUND`
 * (422), `CONNECTION_TEST_FAILED` (500 — a genuine platform-side failure
 * during "test connection," never a rejected credential or unreachable host;
 * research.md D7).
 */

/** Which part of the form a refusal belongs to. */
export type ConnectionErrorField = 'config' | 'form';

class ConnectionError extends Error {
  readonly status: number;
  readonly field: ConnectionErrorField;

  constructor(
    name: string,
    status: number,
    field: ConnectionErrorField,
    message: string,
  ) {
    super(message);
    this.name = name;
    this.status = status;
    this.field = field;
  }
}

/** No connection with that id exists in this workspace. */
export class ConnectionNotFoundError extends ConnectionError {
  constructor(message?: string) {
    super(
      'ConnectionNotFoundError',
      404,
      'form',
      message ?? 'That connection no longer exists.',
    );
  }
}

/** The workspace already has a connection of this provider — GitHub and GitLab
 *  allow one each (connections rework). */
export class ConnectionAlreadyExistsError extends ConnectionError {
  constructor(message?: string) {
    super(
      'ConnectionAlreadyExistsError',
      409,
      'form',
      message ?? 'This provider is already connected in this workspace.',
    );
  }
}

/** `config` fails the type-matched Pydantic model (FR-002). */
export class InvalidConnectionConfigError extends ConnectionError {
  constructor(message?: string) {
    super(
      'InvalidConnectionConfigError',
      422,
      'config',
      message ??
        'The service did not accept this configuration. Check each entry.',
    );
  }
}

/** `config`'s secret-key reference does not exist in this workspace (FR-003). */
export class SecretReferenceNotFoundError extends ConnectionError {
  constructor(message?: string) {
    super(
      'SecretReferenceNotFoundError',
      422,
      'config',
      message ??
        'That secret no longer exists in this workspace. Choose another.',
    );
  }
}

/**
 * A genuine platform-side failure while testing a connection — never a
 * rejected external credential or unreachable host, which the endpoint
 * reports as a normal `200 { ok: false }` result instead (research.md D7).
 */
export class ConnectionTestFailedError extends ConnectionError {
  constructor(message?: string) {
    super(
      'ConnectionTestFailedError',
      500,
      'form',
      message ?? 'The test could not run. Please try again.',
    );
  }
}

/**
 * A refusal this build does not recognise — a rule the service grew later.
 * Deliberately vague: claiming a cause it cannot know would be worse than
 * admitting it does not know one.
 */
export class ConnectionRequestFailedError extends ConnectionError {
  constructor(status: number, message?: string) {
    super(
      'ConnectionRequestFailedError',
      status,
      'form',
      message ?? 'The request failed. Please try again.',
    );
  }
}

const BY_CODE: Record<string, (message?: string) => ConnectionError> = {
  CONNECTION_NOT_FOUND: (m) => new ConnectionNotFoundError(m),
  CONNECTION_ALREADY_EXISTS: (m) => new ConnectionAlreadyExistsError(m),
  CONNECTION_CONFIG_INVALID: (m) => new InvalidConnectionConfigError(m),
  CONNECTION_SECRET_REFERENCE_NOT_FOUND: (m) =>
    new SecretReferenceNotFoundError(m),
  CONNECTION_TEST_FAILED: (m) => new ConnectionTestFailedError(m),
};

/**
 * Map one error response to its typed refusal.
 *
 * The service's own `message` is **not** passed through, for the same reason
 * `toTriggerCreateError`/`toSecretError` don't: the classes carry messages
 * written for the person looking at the form, not for a log reader. The
 * envelope's `error` code is what this reads.
 *
 * One exception: `CONNECTION_CONFIG_INVALID`. Its message is the single thing
 * that tells the person which entry to change ("Start the address with
 * https://…"), and the service writes it as a plain sentence for exactly that.
 * A generic line there is what left a rejected address unexplained.
 */
export function toConnectionError(status: number, body: unknown): Error {
  const code =
    typeof body === 'object' && body !== null && 'error' in body
      ? (body as ErrorEnvelope).error
      : undefined;

  const construct = code ? BY_CODE[code] : undefined;
  if (code === 'CONNECTION_CONFIG_INVALID') {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? (body as ErrorEnvelope).message
        : undefined;
    return new InvalidConnectionConfigError(
      typeof message === 'string' && message.trim() !== ''
        ? message
        : undefined,
    );
  }
  return construct ? construct() : new ConnectionRequestFailedError(status);
}

/** Narrows a mutation's `unknown` error to a typed refusal (constitution §2.5). */
export function isConnectionError(error: unknown): error is ConnectionError {
  return error instanceof ConnectionError;
}

export type { ConnectionError };
